import React, { useEffect, useRef, useState } from "react";
import { useCart } from "../context/CartContext";
import { useTranslation } from "react-i18next";
import { toast, ToastContainer } from "react-toastify";
import { useNavigate } from "react-router-dom";
import api from "../services/api";
import { getShippingConfig, calcShipping, isFirstOrderFreeShipping, clearShippingCache } from "../services/shipping";
import { isMobileDevice, startMobileInicisPayment } from "../services/inicisMobile";
import AddressSearch from "../components/AddressSearch";
import { isValidKoreanMobile, formatKoreanMobile } from "../services/phone";
import { trackBeginCheckout } from "../services/analytics";
import "react-toastify/dist/ReactToastify.css";

/* ─── Inline styles ──────────────────────────────────────────────────────────── */
const S = `
@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@600;700&family=DM+Sans:wght@400;500;600&display=swap');

.uco-page {
  background: #f9f7f4;
  min-height: 100vh;
  padding: 40px 0 80px;
  font-family: 'DM Sans', sans-serif;
}
.uco-inner {
  max-width: 1080px;
  margin: 0 auto;
  padding: 0 20px;
}
.uco-title {
  font-family: 'Playfair Display', serif;
  font-size: 1.9rem;
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 28px;
}
.uco-grid {
  display: grid;
  grid-template-columns: 1fr 380px;
  gap: 24px;
  align-items: start;
}

/* ─── Cards ─── */
.uco-card {
  background: #fff;
  border-radius: 18px;
  border: 1px solid #e8e4df;
  padding: 24px;
  margin-bottom: 20px;
}
.uco-card:last-child { margin-bottom: 0; }
.uco-card-title {
  font-family: 'Playfair Display', serif;
  font-size: 1.1rem;
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
}
.uco-label {
  display: block;
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #1a5c38;
  margin-bottom: 7px;
}

/* ─── Address select ─── */
.uco-addr-select {
  width: 100%;
  border: 1.5px solid #e8e4df;
  border-radius: 11px;
  padding: 11px 14px;
  font-family: 'DM Sans', sans-serif;
  font-size: 0.9rem;
  color: #1a1a1a;
  background: #fff;
  outline: none;
  cursor: pointer;
  transition: border-color 0.18s;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%238a8a8a' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 14px center;
  padding-right: 36px;
}
.uco-addr-select:focus { border-color: #1a5c38; }

/* ─── Address card display ─── */
.uco-addr-card {
  background: #f9f7f4;
  border-radius: 12px;
  border: 1px solid #e8e4df;
  padding: 14px 16px;
  margin-top: 12px;
  font-size: 0.87rem;
  color: #4a4a4a;
  line-height: 1.6;
}
.uco-addr-card strong { color: #1a1a1a; }
.uco-addr-actions {
  display: flex;
  gap: 8px;
  margin-top: 10px;
}
.uco-addr-add-btn {
  display: flex;
  align-items: center;
  gap: 7px;
  margin-top: 12px;
  background: none;
  border: 1.5px dashed #1a5c38;
  border-radius: 11px;
  padding: 10px 16px;
  font-family: 'DM Sans', sans-serif;
  font-size: 0.88rem;
  font-weight: 600;
  color: #1a5c38;
  cursor: pointer;
  width: 100%;
  justify-content: center;
  transition: background 0.15s;
}
.uco-addr-add-btn:hover { background: #e8f3ed; }

/* ─── Inputs ─── */
.uco-input {
  width: 100%;
  border: 1.5px solid #e8e4df;
  border-radius: 11px;
  padding: 10px 14px;
  font-family: 'DM Sans', sans-serif;
  font-size: 0.9rem;
  color: #1a1a1a;
  background: #fff;
  outline: none;
  transition: border-color 0.18s;
  margin-bottom: 12px;
}
.uco-input:focus { border-color: #1a5c38; }
.uco-input::placeholder { color: #bbb; }
textarea.uco-input { resize: vertical; min-height: 80px; margin-bottom: 0; }
.uco-row { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }

/* ─── Payment method ─── */
.uco-pay-options {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.uco-pay-option {
  border: 1.5px solid #e8e4df;
  border-radius: 12px;
  padding: 13px 16px;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
  display: flex;
  align-items: center;
  gap: 12px;
}
.uco-pay-option.selected {
  border-color: #1a5c38;
  background: #e8f3ed;
}
.uco-pay-option.disabled {
  cursor: not-allowed;
  opacity: 0.7;
  background: #faf8f4;
  border-style: dashed;
}
.uco-soon-badge {
  display: inline-block;
  margin-left: 8px;
  font-size: 0.66rem;
  font-weight: 700;
  letter-spacing: .03em;
  text-transform: uppercase;
  color: #b45309;
  background: #fef3c7;
  border-radius: 999px;
  padding: 2px 8px;
  vertical-align: middle;
}
.uco-pay-option input[type="radio"] {
  accent-color: #1a5c38;
  width: 16px; height: 16px;
  flex-shrink: 0;
}
.uco-pay-option-label {
  font-size: 0.9rem;
  font-weight: 600;
  color: #1a1a1a;
}
.uco-pay-option-desc {
  font-size: 0.78rem;
  color: #8a8a8a;
  margin-top: 2px;
}

/* ─── Payment detail boxes ─── */
.uco-pay-detail {
  border-radius: 12px;
  padding: 16px;
  margin-top: 14px;
  font-size: 0.87rem;
}
.uco-pay-detail.transfer { background: #f0f9f4; border: 1px solid #c5dece; }
.uco-pay-detail.card     { background: #f0f4ff; border: 1px solid #c5d0f0; }
.uco-pay-detail-title {
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 10px;
  font-size: 0.9rem;
}
.uco-bank-row {
  display: flex;
  justify-content: space-between;
  padding: 5px 0;
  border-bottom: 1px solid rgba(0,0,0,0.06);
  font-size: 0.86rem;
}
.uco-bank-row:last-of-type { border-bottom: none; }
.uco-bank-row span:first-child { color: #6a6a6a; }
.uco-bank-row span:last-child  { font-weight: 600; color: #1a1a1a; }
.uco-account-no { color: #1a5c38 !important; font-size: 1rem !important; }
.uco-info-box {
  margin-top: 10px;
  background: rgba(255,255,255,0.7);
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 0.82rem;
  color: #4a4a4a;
  line-height: 1.5;
}
.uco-card-checks { display: flex; flex-direction: column; gap: 6px; margin-bottom: 12px; }
.uco-card-check { display: flex; align-items: center; gap: 8px; font-size: 0.84rem; color: #4a4a4a; }
.uco-card-check-dot { width: 18px; height: 18px; background: #e8f3ed; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 0.65rem; color: #1a5c38; flex-shrink: 0; }
.whatsapp-notice {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  background: #fff8e1;
  border: 1px solid #ffe082;
  border-radius: 11px;
  padding: 12px 14px;
  margin-top: 14px;
  font-size: 0.84rem;
  color: #4a4a4a;
}
.whatsapp-icon { font-size: 1.4rem; flex-shrink: 0; margin-top: 1px; }

/* ─── Order summary ─── */
.uco-summary-card {
  background: #fff;
  border-radius: 18px;
  border: 1px solid #e8e4df;
  padding: 24px;
  position: sticky;
  top: 88px;
}
.uco-summary-title {
  font-family: 'Playfair Display', serif;
  font-size: 1.2rem;
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 18px;
}
.uco-order-item {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 8px 0;
  border-bottom: 1px solid #f0ede9;
  gap: 12px;
}
.uco-order-item:last-of-type { border-bottom: none; }
.uco-order-item-name { font-size: 0.85rem; color: #4a4a4a; flex: 1; line-height: 1.3; }
.uco-order-item-price { font-size: 0.85rem; font-weight: 600; color: #1a1a1a; white-space: nowrap; }
.uco-divider { height: 1px; background: #e8e4df; margin: 12px 0; }
.uco-sum-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 0;
  font-size: 0.9rem;
  color: #4a4a4a;
}
.uco-sum-row.total {
  font-size: 1rem;
  font-weight: 700;
  color: #1a1a1a;
  border-top: 2px solid #e8e4df;
  padding-top: 12px;
  margin-top: 4px;
}
.uco-sum-row .free { color: #1a5c38; font-weight: 600; }
.uco-sum-row .discount { color: #1a5c38; }
.uco-first-order-perk {
  background: #eef6f0;
  color: #1a5c38;
  border-radius: 8px;
  padding: 7px 10px;
  font-size: 0.78rem;
  font-weight: 600;
  margin: 2px 0 8px;
}

/* ─── Promo ─── */
.uco-promo-wrap {
  display: flex;
  gap: 8px;
  margin-top: 14px;
}
.uco-promo-input {
  flex: 1;
  border: 1.5px solid #e8e4df;
  border-radius: 10px;
  padding: 9px 13px;
  font-family: 'DM Sans', sans-serif;
  font-size: 0.88rem;
  outline: none;
  transition: border-color 0.18s;
}
.uco-promo-input:focus { border-color: #1a5c38; }
.uco-promo-btn {
  background: #1a5c38; color: #fff;
  border: none; border-radius: 10px;
  padding: 9px 16px;
  font-family: 'DM Sans', sans-serif;
  font-size: 0.88rem; font-weight: 600;
  cursor: pointer; white-space: nowrap;
  transition: background 0.15s;
}
.uco-promo-btn:hover { background: #134429; }
.uco-promo-applied {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #e8f3ed;
  border-radius: 10px;
  padding: 10px 14px;
  margin-top: 14px;
  font-size: 0.87rem;
}
.uco-promo-applied span { color: #1a5c38; font-weight: 600; }
.uco-promo-remove {
  background: none; border: none;
  color: #d94f3d; font-size: 0.82rem;
  cursor: pointer; font-weight: 600;
  font-family: 'DM Sans', sans-serif;
  transition: opacity 0.13s;
}
.uco-promo-remove:hover { opacity: 0.7; }

/* ─── Place order button ─── */
.uco-place-btn {
  width: 100%;
  margin-top: 20px;
  background: #1a5c38; color: #fff;
  border: none; border-radius: 13px;
  padding: 15px;
  font-family: 'DM Sans', sans-serif;
  font-size: 1rem; font-weight: 700;
  cursor: pointer; transition: background 0.15s;
  display: flex; align-items: center; justify-content: center; gap: 8px;
}
.uco-place-btn:hover:not(:disabled) { background: #134429; }
.uco-place-btn:disabled { background: #a0c4b0; cursor: not-allowed; }

/* ─── Address modal overlay ─── */
.addr-modal-overlay {
  position: fixed; inset: 0;
  background: rgba(0,0,0,0.4);
  backdrop-filter: blur(3px);
  z-index: 3000;
  display: flex; align-items: center; justify-content: center;
  padding: 20px;
  animation: fadeIn 0.16s ease;
}
@keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
.addr-modal {
  background: #fff;
  border-radius: 20px;
  padding: 28px 24px;
  width: 100%; max-width: 480px;
  box-shadow: 0 20px 60px rgba(0,0,0,0.14);
  max-height: 90vh;
  overflow-y: auto;
  animation: modalUp 0.18s ease;
}
@keyframes modalUp { from { transform: translateY(16px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
.addr-modal-title {
  font-family: 'Playfair Display', serif;
  font-size: 1.2rem; font-weight: 700;
  color: #1a1a1a; margin-bottom: 20px;
}
.addr-modal-btns { display: flex; gap: 10px; margin-top: 20px; }
.addr-modal-cancel {
  flex: 1; background: #f5f5f5; color: #4a4a4a;
  border: none; border-radius: 11px; padding: 12px;
  font-family: 'DM Sans', sans-serif; font-size: 0.9rem; font-weight: 600;
  cursor: pointer; transition: background 0.13s;
}
.addr-modal-cancel:hover { background: #ebebeb; }
.addr-modal-save {
  flex: 1; background: #1a5c38; color: #fff;
  border: none; border-radius: 11px; padding: 12px;
  font-family: 'DM Sans', sans-serif; font-size: 0.9rem; font-weight: 600;
  cursor: pointer; transition: background 0.13s;
}
.addr-modal-save:hover { background: #134429; }

/* Spinner */
.spin-icon { animation: spin 1s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }

/* Responsive */
@media (max-width: 900px) {
  .uco-grid { grid-template-columns: 1fr; }
  .uco-summary-card { position: static; }
}
@media (max-width: 480px) {
  .uco-inner { padding: 0 14px; }
  .uco-title  { font-size: 1.5rem; }
  .uco-row    { grid-template-columns: 1fr; gap: 0; }
}
`;

/* ─── Address Modal Component ──────────────────────────────────────────────── */
const AddressModal = ({ onClose, onSaved }) => {
  const { t } = useTranslation();
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [form, setForm] = useState({
    full_name: "", phone: "", address_type: "shipping",
    postal_code: "", road_address: "", address_line: "", address_details: "",
    sido: "", sigungu: "", bname: "", building_name: "", address_english: "",
    address_source: "manual", country: "South Korea",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleAddressChange = (patch) => {
    setForm(prev => ({ ...prev, ...patch }));
    setErrors({});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const errs = {};
    if (!form.postal_code || !(form.road_address || form.address_line))
      errs.address = t("address.required");
    if (!form.address_details?.trim())
      errs.detail = t("address.detailRequired");
    if (!isValidKoreanMobile(form.phone)) {
      toast.error(t("auth.invalidPhone"));
      return;
    }
    setErrors(errs);
    if (Object.keys(errs).length) return;

    setSaving(true);
    try {
      await api.post("/addresses", form);
      toast.success(t("checkout.addressAdded"));
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.message || t("checkout.addressSaveFailed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="addr-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="addr-modal">
        <div className="addr-modal-title">{t("checkout.addAddressTitle")}</div>
        <form onSubmit={handleSubmit}>
          <span className="uco-label">{t("checkout.fullName")}</span>
          <input className="uco-input" name="full_name" value={form.full_name}
            onChange={handleChange} placeholder={t("checkout.fullNamePlaceholder")} required />

          <AddressSearch
            value={form}
            onChange={handleAddressChange}
            error={errors.address}
            detailError={errors.detail}
          />

          <span className="uco-label">{t("checkout.phone")}</span>
          <input className="uco-input" name="phone" type="tel" value={form.phone}
            onChange={(e) => setForm(p => ({ ...p, phone: formatKoreanMobile(e.target.value) }))}
            placeholder="010-1234-5678" inputMode="numeric" maxLength={13} required />

          <div className="addr-modal-btns">
            <button type="button" className="addr-modal-cancel" onClick={onClose}>{t("orders.cancel")}</button>
            <button type="submit" className="addr-modal-save" disabled={saving}>
              {saving ? t("checkout.saving") : t("checkout.saveAddress")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

/* ─── Main UserCheckoutPage ────────────────────────────────────────────────── */
const UserCheckoutPage = () => {
  const { cartItems, clearCart } = useCart();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [addresses,         setAddresses]         = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState("");
  const [showAddressModal,  setShowAddressModal]  = useState(false);
  const [promoCode,         setPromoCode]         = useState("");
  const [promoDiscount,     setPromoDiscount]     = useState(0);
  const [discountType,      setDiscountType]      = useState("flat");
  const [subtotal,          setSubtotal]          = useState(0);
  const [shipping,          setShipping]          = useState(0);
  const [total,             setTotal]             = useState(0);
  const [walletBalance,     setWalletBalance]     = useState(0);
  const [loyalty,           setLoyalty]           = useState({ enabled: false });
  const [pointsToRedeem,    setPointsToRedeem]    = useState(0);
  const [note,              setNote]              = useState("");
  const [isPlacing,         setIsPlacing]         = useState(false);
  const [promoApplied,      setPromoApplied]      = useState(false);
  const [paymentMethod,     setPaymentMethod]     = useState("account_transfer");
  const [depositorName,     setDepositorName]     = useState("");
  const [payData,           setPayData]           = useState(null);
  // Which methods the super-admin currently allows (card defaults off).
  const [payMethods,        setPayMethods]        = useState({ card_enabled: false, transfer_enabled: true });

  // ─── INIStdPay check ───────────────────────────────────────────────────────
  useEffect(() => {
    setTimeout(() => {
      if (typeof window.INIStdPay !== "undefined") console.log("INIStdPay loaded");
      else console.warn("INIStdPay not loaded");
    }, 1000);
  }, []);

  // ─── Fetch addresses ───────────────────────────────────────────────────────
  const fetchAddresses = async () => {
    try {
      const res = await api.get("/addresses");
      const list = res.data || [];
      setAddresses(list);
      if (list.length > 0 && !selectedAddressId) {
        // prefer default, otherwise first
        const def = list.find(a => a.is_default_shipping) || list[0];
        setSelectedAddressId(def._id);
      }
    } catch {
      toast.error(t("checkout.addressLoadFailed"));
    }
  };

  useEffect(() => { fetchAddresses(); }, []);

  // ─── Calculate totals ──────────────────────────────────────────────────────
  useEffect(() => {
    let s = 0;
    cartItems.forEach(item => {
      const price = item.product_id?.is_on_sale
        ? item.product_id?.sale_price
        : item.product_id?.sell_price;
      s += (price || 0) * (item.quantity || 0);
    });
    setSubtotal(s);
  }, [cartItems]);

  const [shipCfg, setShipCfg] = useState({ fee: 5000, threshold: 50000 });

  // Loyalty wallet balance + program config (for points redemption) + shipping.
  useEffect(() => {
    api.get("/loyalty/public").then((r) => setLoyalty(r.data)).catch(() => {});
    api.get("/loyalty/wallet").then((r) => setWalletBalance(r.data.balance || 0)).catch(() => {});
    getShippingConfig().then(setShipCfg);
    api.get("/settings/payment-methods")
      .then((r) => {
        const cfg = { card_enabled: !!r.data.card_enabled, transfer_enabled: !!r.data.transfer_enabled };
        setPayMethods(cfg);
        // If the pre-selected method is disabled, jump to an enabled one.
        setPaymentMethod((cur) =>
          (cur === "inicis_card" && !cfg.card_enabled) ? "account_transfer"
          : (cur === "account_transfer" && !cfg.transfer_enabled && cfg.card_enabled) ? "inicis_card"
          : cur
        );
      })
      .catch(() => {});
  }, []);

  const methodEnabled = (v) =>
    v === "inicis_card" ? payMethods.card_enabled
    : v === "account_transfer" ? payMethods.transfer_enabled
    : true;

  // Points redemption (both payment paths for logged-in users), capped by the
  // wallet balance and the admin's max-% rule (base matches the server: subtotal).
  const redeemRate = Number(loyalty.redeem_won_per_point || 0);
  const canRedeem = loyalty.enabled && walletBalance > 0 && redeemRate > 0;
  const maxRedeemablePoints = canRedeem
    ? Math.min(walletBalance, Math.floor(Math.floor(subtotal * (loyalty.max_redeem_percent ?? 100) / 100) / redeemRate))
    : 0;
  const appliedPoints = Math.min(Math.max(Number(pointsToRedeem) || 0, 0), maxRedeemablePoints);
  const pointsDiscount = canRedeem ? appliedPoints * redeemRate : 0;

  useEffect(() => {
    let disc = discountType === "percent"
      ? (subtotal * promoDiscount) / 100
      : promoDiscount;
    const after    = Math.max(subtotal - disc - pointsDiscount, 0);
    const shipFee  = calcShipping(after, shipCfg);
    setShipping(shipFee);
    setTotal(after + shipFee);
  }, [subtotal, promoDiscount, discountType, pointsDiscount, shipCfg]);

  // Fire begin_checkout exactly once per visit, the first time a total is
  // ready — not on every recalc (promo, points, shipping) or it'd repeat.
  const beganCheckoutRef = useRef(false);
  useEffect(() => {
    if (!beganCheckoutRef.current && cartItems.length > 0 && total > 0) {
      beganCheckoutRef.current = true;
      trackBeginCheckout(cartItems, total);
    }
  }, [cartItems, total]);

  // ─── Promo ─────────────────────────────────────────────────────────────────
  const handlePromo = async () => {
    if (!promoCode.trim()) return toast.warning(t("checkout.enterPromo"));
    try {
      const res   = await api.post("/promos/validate", { code: promoCode.trim(), order_total: subtotal });
      const promo = res.data;
      if (promo.valid) {
        setPromoApplied(true);
        // Store the RAW discount value; the total effect scales percent codes
        // by the live subtotal itself (using the pre-computed amount here would
        // double-apply and overshoot the whole order).
        setPromoDiscount(promo.discount_value);
        setDiscountType(promo.discount_type);
        toast.success(
          promo.discount_type === "percent"
            ? `Promo applied: ${promo.discount_value}% off`
            : `Promo applied: ₩${Number(promo.discount_value).toLocaleString()} off`
        );
      } else {
        toast.error(promo.message || "Invalid promo code");
      }
    } catch (err) {
      // Show the backend's specific reason (expired / inactive / min order / …).
      toast.error(err.response?.data?.message || "Failed to validate promo code");
    }
  };

  const removePromo = () => {
    setPromoApplied(false);
    setPromoCode("");
    setPromoDiscount(0);
    setDiscountType("flat");
  };

  // ─── INIpay helpers ────────────────────────────────────────────────────────
  const buildGoodName = () => {
    const names = cartItems.slice(0, 2).map(i => i.product_id?.name || "Product").join(", ");
    return cartItems.length > 2 ? `${names} 외 ${cartItems.length - 2}건` : names;
  };

  const getSelectedAddress = () => addresses.find(a => a._id === selectedAddressId) || {};

  const prepareINIpayData = async () => {
    const res = await api.get(`/inicis/prepare?price=${total}`);
    const addr = getSelectedAddress();
    return {
      ...res.data,
      goodname:   buildGoodName(),
      buyername:  addr.full_name || "",
      buyertel:   (addr.phone || "").replace(/[^0-9]/g, ""),
      buyeremail: addr.email || "",
    };
  };

  // ─── INIpay payment ────────────────────────────────────────────────────────
  const handleINIpayPayment = async () => {
    if (!payMethods.card_enabled) { toast.info(t("checkout.cardComingSoon")); return; }
    if (!selectedAddressId) { toast.error(t("checkout.selectAddressError")); return; }
    if (cartItems.length === 0) { toast.error(t("checkout.cartEmpty")); return; }
    if (!isMobileDevice() && typeof window.INIStdPay === "undefined") { toast.error(t("checkout.paymentModuleError")); return; }
    setIsPlacing(true);
    try {
      const params = await prepareINIpayData();
      const addr = getSelectedAddress();
      sessionStorage.setItem("pendingOrder", JSON.stringify({
        addressId: selectedAddressId,
        fullName: addr.full_name || "",
        phone: addr.phone || "",
        address: addr.street || "",
        city: addr.city || "",
        promoCode: promoApplied ? promoCode : null,
        discount: promoDiscount, shipping, subtotal, total, note,
        pointsToRedeem: appliedPoints,
        isLoggedInUser: true,
        cartItems: cartItems.map(item => ({
          productId:   item.product_id?._id,
          productName: item.product_id?.name || "Product",
          quantity:    item.quantity,
          price:       item.product_id?.is_on_sale ? item.product_id?.sale_price : item.product_id?.sell_price || 0,
        })),
      }));
      // Phones can't use the PC INIStdPay window — go through the Inicis
      // mobile flow instead (full-page redirect; returns on /payment/success).
      if (isMobileDevice()) {
        startMobileInicisPayment(params);
        return;
      }
      setPayData(params);
      setTimeout(() => { window.INIStdPay.pay("SendPayForm_id"); setIsPlacing(false); }, 100);
    } catch {
      toast.error(t("checkout.paymentInitFailed"));
      setIsPlacing(false);
    }
  };

  // ─── Traditional order ─────────────────────────────────────────────────────
  const handleTraditionalOrder = async () => {
    if (!selectedAddressId) return toast.error(t("checkout.selectAddressError"));
    if (cartItems.length === 0) return toast.error(t("checkout.cartEmpty"));
    if (paymentMethod === "account_transfer" && !depositorName.trim()) return toast.error(t("checkout.depositorRequired"));
    setIsPlacing(true);
    try {
      const res = await api.post("/orders", {
        shipping_address_id: selectedAddressId,
        billing_address_id:  selectedAddressId,
        payment_method:      paymentMethod,
        note,
        promo_code:          promoApplied ? promoCode : null,
        depositor_name:      depositorName,
        points_to_redeem:    appliedPoints,
      });
      const orderId     = res.data.order?._id || res.data._id;      // Mongo _id (used by upload-proof)
      const orderNumber = res.data.order_id || res.data.order?.order_id; // FM-YYYY-MM-DD-#### (display)
      clearCart();
      clearShippingCache(); // first-order perk is spent now — re-fetch next time
      toast.success(t("checkout.orderPlaced"));
      setTimeout(() => navigate("/order-success", { state: { orderId, orderNumber, total } }), 100);
    } catch (err) {
      toast.error(err.response?.data?.message || err.response?.data?.error || "Failed to place order");
    } finally {
      setIsPlacing(false);
    }
  };

  const handlePlaceOrder = () => {
    if (paymentMethod === "inicis_card") handleINIpayPayment();
    else handleTraditionalOrder();
  };

  const selectedAddr = getSelectedAddress();

  return (
    <>
      <style>{S}</style>
      <div className="uco-page">
        <ToastContainer position="bottom-right" autoClose={3000} />

        {/* ─── Address modal ─── */}
        {showAddressModal && (
          <AddressModal
            onClose={() => setShowAddressModal(false)}
            onSaved={() => {
              fetchAddresses();
            }}
          />
        )}

        {/* ─── INIpay hidden form ─── */}
        {payData && (
          <form id="SendPayForm_id" method="post" style={{ display: "none" }}>
            <input type="hidden" name="version"      value="1.0" />
            <input type="hidden" name="mid"          value={payData.mid} />
            <input type="hidden" name="oid"          value={payData.oid} />
            <input type="hidden" name="price"        value={payData.price} />
            <input type="hidden" name="timestamp"    value={payData.timestamp} />
            <input type="hidden" name="use_chkfake"  value={payData.use_chkfake ?? "Y"} />
            <input type="hidden" name="signature"    value={payData.signature} />
            <input type="hidden" name="verification" value={payData.verification} />
            <input type="hidden" name="mKey"         value={payData.mKey} />
            <input type="hidden" name="currency"     value="WON" />
            <input type="hidden" name="gopaymethod"  value="Card:DirectBank:VBank:HPP" />
            <input type="hidden" name="goodname"     value={payData.goodname ?? buildGoodName()} />
            <input type="hidden" name="buyername"    value={payData.buyername ?? ""} />
            <input type="hidden" name="buyertel"     value={payData.buyertel ?? ""} />
            <input type="hidden" name="buyeremail"   value={payData.buyeremail ?? ""} />
            <input type="hidden" name="acceptmethod" value={payData.acceptmethod ?? "HPP(1):va_receipt:below1000:centerCd(Y)"} />
            <input type="hidden" name="returnUrl"    value={`${window.location.origin}/api/inicis/return`} />
            <input type="hidden" name="closeUrl"     value={`${window.location.origin}/close.html`} />
          </form>
        )}

        <div className="uco-inner">
          <h1 className="uco-title">{t("checkout.title")}</h1>

          <div className="uco-grid">
            {/* ─── LEFT COLUMN ─── */}
            <div>

              {/* ── Delivery Address ── */}
              <div className="uco-card">
                <div className="uco-card-title">📍 {t("checkout.deliveryAddress")}</div>

                {addresses.length === 0 ? (
                  /* No addresses at all */
                  <div style={{ textAlign: "center", padding: "20px 0" }}>
                    <div style={{ fontSize: "2rem", marginBottom: "10px" }}>📭</div>
                    <div style={{ fontSize: "0.9rem", color: "#8a8a8a", marginBottom: "16px" }}>
                      {t("checkout.noAddresses")}
                    </div>
                    <button className="uco-addr-add-btn" onClick={() => setShowAddressModal(true)}>
                      + {t("checkout.addFirstAddress")}
                    </button>
                  </div>
                ) : (
                  <>
                    {/* Address dropdown */}
                    <span className="uco-label">{t("checkout.selectAddress")}</span>
                    <select
                      className="uco-addr-select"
                      value={selectedAddressId}
                      onChange={e => setSelectedAddressId(e.target.value)}
                    >
                      {addresses.map(addr => (
                        <option key={addr._id} value={addr._id}>
                          {addr.full_name} — {addr.street}, {addr.city}
                          {addr.is_default_shipping ? ` ★ ${t("checkout.default")}` : ""}
                        </option>
                      ))}
                    </select>

                    {/* Selected address preview */}
                    {selectedAddr.street && (
                      <div className="uco-addr-card">
                        <strong>{selectedAddr.full_name}</strong><br />
                        {selectedAddr.postal_code && `[${selectedAddr.postal_code}] `}
                        {selectedAddr.road_address || selectedAddr.address_line || selectedAddr.street}<br />
                        {selectedAddr.address_details && <>{selectedAddr.address_details}<br /></>}
                        📞 {selectedAddr.phone}
                      </div>
                    )}

                    {/* Add new address button */}
                    <button
                      className="uco-addr-add-btn"
                      onClick={() => setShowAddressModal(true)}
                      style={{ marginTop: "14px" }}
                    >
                      + {t("checkout.addAddressTitle")}
                    </button>
                  </>
                )}
              </div>

              {/* ── Order Notes ── */}
              <div className="uco-card">
                <div className="uco-card-title">📝 {t("checkout.orderNotes")}</div>
                <span className="uco-label">{t("checkout.specialInstructions")}</span>
                <textarea
                  className="uco-input"
                  value={note}
                  onChange={e => setNote(e.target.value)}
                  placeholder={t("checkout.instructionsPlaceholder")}
                  rows={3}
                />
              </div>

              {/* ── Payment Method ── */}
              <div className="uco-card">
                <div className="uco-card-title">💳 {t("success.paymentMethod")}</div>

                <div className="uco-pay-options">
                  {[
                    { value: "account_transfer", emoji: "🏦", label: t("success.accountTransfer"), desc: t("checkout.transferDesc") },
                    { value: "inicis_card",      emoji: "💳", label: t("success.cardPayment"),     desc: t("checkout.cardDesc") },
                  ].filter(opt => methodEnabled(opt.value) || opt.value === "inicis_card").map(opt => {
                    const enabled = methodEnabled(opt.value);
                    return (
                      <label
                        key={opt.value}
                        className={`uco-pay-option ${paymentMethod === opt.value ? "selected" : ""} ${enabled ? "" : "disabled"}`}
                        onClick={(e) => { if (!enabled) { e.preventDefault(); toast.info(t("checkout.cardComingSoon")); } }}
                      >
                        <input
                          type="radio"
                          name="payment"
                          value={opt.value}
                          checked={paymentMethod === opt.value}
                          disabled={!enabled}
                          onChange={() => enabled && setPaymentMethod(opt.value)}
                        />
                        <div>
                          <div className="uco-pay-option-label">
                            {opt.emoji} {opt.label}
                            {!enabled && <span className="uco-soon-badge">{t("checkout.comingSoon")}</span>}
                          </div>
                          <div className="uco-pay-option-desc">
                            {enabled ? opt.desc : t("checkout.cardComingSoonDesc")}
                          </div>
                        </div>
                      </label>
                    );
                  })}
                </div>

                {/* ── Account Transfer detail ── */}
                {paymentMethod === "account_transfer" && (
                  <div className="uco-pay-detail transfer">
                    <div className="uco-pay-detail-title">🏦 {t("checkout.bankDetails")}</div>
                    <div className="uco-bank-row"><span>{t("checkout.bank")}</span><span>NH 농협은행 (NH Bank)</span></div>
                    <div className="uco-bank-row"><span>{t("checkout.accountTitle")}</span><span>ABBAS AZHAR (패밀리월드마트)</span></div>
                    <div className="uco-bank-row"><span>{t("checkout.accountNo")}</span><span className="uco-account-no">355-0084-6104-43</span></div>
                    <div style={{ marginTop: "14px" }}>
                      <span className="uco-label">{t("checkout.depositorName")} *</span>
                      <input
                        className="uco-input"
                        value={depositorName}
                        onChange={e => setDepositorName(e.target.value)}
                        placeholder={t("checkout.depositorPlaceholder")}
                      />
                    </div>
                    <div className="uco-info-box">
                      {t("checkout.transferNote")}
                    </div>
                  </div>
                )}


                {/* ── Card detail ── */}
                {paymentMethod === "inicis_card" && (
                  <div className="uco-pay-detail card">
                    <div className="uco-pay-detail-title">🔒 {t("checkout.secureCardTitle")}</div>
                    <div className="uco-card-checks">
                      {[t("checkout.check1"), t("checkout.check2"), t("checkout.check3"), t("checkout.check4")].map((c, i) => (
                        <div className="uco-card-check" key={i}>
                          <div className="uco-card-check-dot">✓</div>
                          <span>{c}</span>
                        </div>
                      ))}
                    </div>
                    <div className="uco-info-box">
                      {t("checkout.cardFlowNote")}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* ─── RIGHT: Order Summary ─── */}
            <div className="uco-summary-card">
              <div className="uco-summary-title">{t("cart.orderSummary")}</div>

              {/* Items */}
              {cartItems.map((item, i) => {
                const price = item.product_id?.is_on_sale
                  ? item.product_id?.sale_price
                  : item.product_id?.sell_price;
                return (
                  <div className="uco-order-item" key={i}>
                    <span className="uco-order-item-name">
                      {item.product_id?.name} × {item.quantity}
                    </span>
                    <span className="uco-order-item-price">
                      ₩{((price || 0) * item.quantity).toLocaleString()}
                    </span>
                  </div>
                );
              })}

              <div className="uco-divider" />

              <div className="uco-sum-row">
                <span>{t("cart.subtotal")}</span>
                <span>₩{subtotal.toLocaleString()}</span>
              </div>

              {promoApplied && (
                <div className="uco-sum-row">
                  <span>{t("cart.discount")}</span>
                  <span className="discount">
                    -{discountType === "percent" ? `${promoDiscount}%` : `₩${promoDiscount.toLocaleString()}`}
                  </span>
                </div>
              )}

              {pointsDiscount > 0 && (
                <div className="uco-sum-row">
                  <span>{t("checkout.points")} ({appliedPoints.toLocaleString()})</span>
                  <span className="discount">-₩{pointsDiscount.toLocaleString()}</span>
                </div>
              )}

              <div className="uco-sum-row">
                <span>{t("cart.shipping")}</span>
                <span>{shipping === 0 ? <span className="free">{t("cart.free")}</span> : `₩${shipping.toLocaleString()}`}</span>
              </div>

              {shipping === 0 && isFirstOrderFreeShipping(Math.max(subtotal - pointsDiscount, 0), shipCfg) && (
                <div className="uco-first-order-perk">🎉 {t("checkout.firstOrderFreeDelivery")}</div>
              )}

              <div className="uco-sum-row total">
                <span>{t("cart.total")}</span>
                <span>₩{total.toLocaleString()}</span>
              </div>

              {/* Promo */}
              {!promoApplied ? (
                <div className="uco-promo-wrap">
                  <input
                    className="uco-promo-input"
                    value={promoCode}
                    onChange={e => setPromoCode(e.target.value)}
                    placeholder={t("checkout.promoPlaceholder")}
                    onKeyDown={e => { if (e.key === "Enter") handlePromo(); }}
                  />
                  <button className="uco-promo-btn" onClick={handlePromo}>{t("checkout.apply")}</button>
                </div>
              ) : (
                <div className="uco-promo-applied">
                  <span>🎉 {t("checkout.promoApplied", { code: promoCode })}</span>
                  <button className="uco-promo-remove" onClick={removePromo}>{t("cart.remove")}</button>
                </div>
              )}

              {/* Use points (logged-in; both payment methods) */}
              {canRedeem && (
                <div className="uco-promo-wrap" style={{ flexDirection: "column", alignItems: "stretch", gap: "6px" }}>
                  <div style={{ fontSize: "0.82rem", color: "#555" }}>
                    🎁 {t("checkout.pointsInfo", { balance: walletBalance.toLocaleString(), max: maxRedeemablePoints.toLocaleString(), value: (maxRedeemablePoints * redeemRate).toLocaleString() })}
                  </div>
                  <div style={{ display: "flex", gap: "8px" }}>
                    <input
                      className="uco-promo-input"
                      type="number"
                      min="0"
                      max={maxRedeemablePoints}
                      value={pointsToRedeem}
                      onChange={(e) => setPointsToRedeem(Math.min(Math.max(Number(e.target.value) || 0, 0), maxRedeemablePoints))}
                      placeholder={t("checkout.pointsPlaceholder")}
                    />
                    <button className="uco-promo-btn" type="button" onClick={() => setPointsToRedeem(maxRedeemablePoints)}>{t("checkout.max")}</button>
                  </div>
                </div>
              )}

              {/* Place order */}
              <button
                className="uco-place-btn"
                disabled={isPlacing || cartItems.length === 0 || addresses.length === 0}
                onClick={handlePlaceOrder}
              >
                {isPlacing ? (
                  <>
                    <span className="spin-icon">⟳</span> {t("checkout.processing")}
                  </>
                ) : (
                  t("checkout.placeOrderBtn", { total: total.toLocaleString() })
                )}
              </button>

              {addresses.length === 0 && (
                <div style={{ textAlign: "center", marginTop: "10px", fontSize: "0.82rem", color: "#d94f3d" }}>
                  {t("checkout.addAddressFirst")}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default UserCheckoutPage;